import type { Analytics } from 'firebase/analytics'
import {
  isMarketingTrackingAllowed,
  logMetaEvent,
  logMetaPageView,
} from './marketing'

// Analytics (GA4) is only supported in browser environments with the required
// APIs (cookies, indexedDB). isSupported() guards init the same way messaging
// does, so SSR/unsupported contexts resolve to null instead of throwing.
//
// PRODUCTION ONLY. Until 2026-08-10 this initialized wherever the browser
// supported it, which included `npm run dev` on a developer's laptop. Local
// page loads were being recorded as real users, sessions and page views in the
// live GA property. Two independent conditions, because either one alone has
// a hole: PROD is false under `vite dev` but true for the prerenderer's local
// server, and the hostname check catches that plus any `vite preview` run.
const ANALYTICS_ENVIRONMENT_ENABLED =
  import.meta.env.PROD &&
  typeof window !== 'undefined' &&
  !/^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)

type AnalyticsModule = typeof import('firebase/analytics')

interface AnalyticsClient {
  mod: AnalyticsModule
  analytics: Analytics
}

let clientPromise: Promise<AnalyticsClient | null> | null = null

// The GA SDK (and firebase/app underneath it) is loaded on demand, and only
// once consent is granted. Nothing here is in the critical path of a public
// page: this module is safe to import from the initial bundle.
function getAnalyticsClient(): Promise<AnalyticsClient | null> {
  if (!ANALYTICS_ENVIRONMENT_ENABLED || !isMarketingTrackingAllowed()) {
    return Promise.resolve(null)
  }
  if (!clientPromise) {
    clientPromise = Promise.all([import('firebase/analytics'), import('./firebaseApp')])
      .then(async ([mod, { app }]) => {
        const supported = await mod.isSupported()
        if (!supported) return null
        return {
          mod,
          analytics: mod.initializeAnalytics(app, { config: { send_page_view: false } }),
        }
      })
      .catch(() => null)
  }
  return clientPromise
}

// Fire a GA4 page_view. A single-page app does not reload between routes, so
// GA4 never auto-fires page_view after the first load — the router calls this
// on every route change to record real navigation.
export function logPageView(path: string): void {
  getAnalyticsClient().then((client) => {
    if (!client) return
    client.mod.logEvent(client.analytics, 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    })
  })
  logMetaPageView()
}

// Fire an arbitrary GA4 event (conversions / funnel signals such as
// begin_checkout, start_trial, purchase, generate_lead). Same isSupported guard
// as page views, so SSR/unsupported contexts are a no-op. Marked as key events
// in GA Admin so they surface as conversions in the dashboard analytics.
export function logGaEvent(name: string, params?: Record<string, unknown>): void {
  getAnalyticsClient().then((client) => {
    if (!client) return
    client.mod.logEvent(client.analytics, name, params)
  })
  logMetaEvent(name, params)
}
